import '!style-loader!css-loader!sass-loader!./sql-error.scss'
import VueCodemirror from '@/components/VueCodemirror'
import { CatalogOverplot } from '@/modules/CatalogOverplotModule/CatalogOverplot'
import { CasModule } from '@/modules/ssp_database/CasModule'
import { cas } from '@/modules/ssp_database/CasModule/cas'
import * as CodeMirror from 'codemirror'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { env, envName, Release } from '@/env'
import { store } from '@/store'

const _envName = envName

@Component({
  components: { VueCodemirror },
})
export default class SqlEditor extends Vue {
  @Prop({ required: true })
  module!: CasModule

  sql = require(`raw-loader!./sample_queries/${_envName}/primary_objects.sql`).default
  release = env.releases.default
  rerun = this.release.reruns[0]
  queueMode = false
  noMail = true
  ajaxing = false

  created() {
    const { releaseName, rerun } = this.module.persistent.memo
    if (releaseName && rerun) {
      const release = env.releases.find(r => r.name == releaseName)
      if (release) {
        this.release = release
        this.rerun = rerun
      }
    }
  }

  @Watch('release')
  onReleaseChange() {
    if (this.release.reruns.indexOf(this.rerun) < 0) {
      this.rerun = this.release.reruns[0]!
    }
  }

  @Watch('rerun')
  onRerunChange() {
    this.rememberRerun()
  }

  private rememberRerun() {
    this.module.persistent.memo = {
      releaseName: this.release!.name,
      rerun: this.rerun,
    }
  }

  cmOptions = {
    mode: 'text/x-pgsql',
    theme: 'blackboard',
    tabSize: 4,
    indentUnit: 4,
    viewportMargin: Infinity,
    keyMap: 'sublime',
    extraKeys: {
      'Ctrl-Enter': () => this.submit(),
    },
    lineNumbers: true,
  }

  mounted() {
    this.$watch(() => this.module.showSqlEditor, (isOpen) => {
      isOpen && this.cm.focus()
    })
    // this.$watch(() => [this.release, this.rerun], () => {
    //   this.module.persistent.release = this.release
    //   this.module.persistent.rerun = this.rerun
    // })
  }

  get cm(): CodeMirror.Editor {
    return (this.$refs.cm as any).codemirror
  }

  errorLine?: CodeMirror.LineWidget

  async submit() {
    this.errorLine && this.errorLine.clear()
    this.errorLine = undefined
    store.asyncProgress('Loading Catalog', async () => {
      try {
        const sql = this.expandSql()
        console.info(`SQL=\n${sql}`)
        this.ajaxing = true
        this.queueMode ? await this.enqueue(sql) : await this.preview(sql)
      } catch (e) {
        alert(JSON.stringify(e))
        console.error(e)
        throw e
      } finally {
        this.ajaxing = false
      }
    })
  }

  private async preview(sql: string) {
    const result = await cas.previewJob(this.release.cas_release, sql)
    result.error && this.showError(result.error)
    if (result.preview) {
      const co = CatalogOverplot.fromCasPreview(this.rerun, result.preview)
      store.currentFrame.modules.catalogOverPlot.cos.push(co)
      store.modules.catalogOverPlot.showList = true
      if (result.preview.count > co.length) {
        alert(`Only top ${co.length} records are fetched. Use "Queue" mode to retrive all records.`)
      }
    }
  }

  private async enqueue(sql: string) {
    await cas.enqueueJob(this.release.cas_release, sql, this.noMail)
    this.module.$emit('refresh-cas-job-list')
    this.module.showJobList = true
  }

  private expandSql() {
    const w = ((s) => {
      if (s) {
        const { c0, c1 } = s
        return `boxSearch(coord, ${c0.a.deg}, ${c1.a.deg}, ${c0.d.deg}, ${c1.d.deg})`
      } else {
        return `'t'`
      }
    })(store.modules.tool.rect)
    return this.sql
      .replace(/\$coord_in_selection_box\b/g, w)
      .replace(/\$rerun\b/g, this.rerun)
  }

  private showError(error: string) {
    const w = lineErrorWidget(error)
    if (w) {
      const { line, el } = w
      this.errorLine = this.cm.addLineWidget(line, el, { coverGutter: false } as any)
      this.cm.scrollIntoView({ line, ch: 0 }, 100)
    }
  }

  savePreset() {
    const name = prompt('Name?')
    if (name) {
      this.module.sqlPresets.push({ name, sql: this.sql })
    }
  }

  openSchemaBrowser() {
    window.open('https://hscdata.mtk.nao.ac.jp/schema_browser3/')
  }

  sampleQueries() {
    return [
      {
        name: 'Primary Objects',
        sql: require(`raw-loader!./sample_queries/${_envName}/primary_objects.sql`).default as string,
      },
      {
        name: 'Clean Objects',
        sql: require(`raw-loader!./sample_queries/${_envName}/clean_objects.sql`).default as string,
      },
    ]
  }

  renamePreset(i: number) {
    const newName = prompt(`Name ?`, this.module.sqlPresets[i].name)
    if (newName) {
      this.module.sqlPresets[i].name = newName
    }
  }
}

function lineErrorWidget(error: string) {
  // ERROR:  column "raee" does not exist
  // LINE 12:     ,raee
  //           ^
  // HINT:  Perhaps you meant to reference the column "forced.ra".
  const m = error.match(/^ERROR:\s+(.*?)\n(LINE\s+(\d+):)(.*)\n(.*)(?:\nHINT:\s+(.*))?/)
  const message = RegExp.$1
  const line = Number(RegExp.$3) - 1
  const padding = ' '.repeat(RegExp.$5.length - RegExp.$2.length)
  const hint = RegExp.$6
  if (line >= 0) {
    const el = document.createElement('div')
    el.className = 'sql-error'
    el.innerText = `${padding}${message}${hint ? `\n${padding}hint: ${hint}` : ''}`
    return { line, el }
  }
}
